import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { loginWithEmail } from '../api/authApi'
import { replaceAuthSession } from '../../../services/authSession'
import { getOrCreateClientInstanceId } from '../../../services/clientInstance'
import { ROUTES } from '../../../constants/routes'
import { getApiErrorMessage } from '../../../utils/apiError'
import AuthLayout from './AuthLayout'
import * as S from './AuthForm.styles'

const TOUCH_PRIMARY_INPUT_QUERY = '(hover: none) and (pointer: coarse)'

function SignInPage() {
  const navigate = useNavigate()
  const emailInputRef = useRef(null)
  const [inputs, setInputs] = useState({ email: '', password: '' })
  const [fieldErrors, setFieldErrors] = useState({})
  const [submitError, setSubmitError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const usesTouchAsPrimaryInput = window.matchMedia?.(TOUCH_PRIMARY_INPUT_QUERY).matches
    if (!usesTouchAsPrimaryInput) emailInputRef.current?.focus({ preventScroll: true })
  }, [])

  const updateInput = (event) => {
    const { name, value } = event.target
    setInputs((current) => ({ ...current, [name]: value }))
    setFieldErrors((current) => ({ ...current, [name]: '' }))
    setSubmitError('')
  }

  const validate = () => {
    const nextErrors = {}
    const email = inputs.email.trim()

    if (!email) nextErrors.email = '이메일을 입력해 주세요.'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) nextErrors.email = '이메일 형식을 확인해 주세요.'
    if (!inputs.password) nextErrors.password = '비밀번호를 입력해 주세요.'

    setFieldErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const reqSignInPage = async (event) => {
    event.preventDefault()
    if (isSubmitting || !validate()) return

    const clientInstanceId = getOrCreateClientInstanceId()
    setIsSubmitting(true)
    setSubmitError('')

    try {
      const result = await loginWithEmail(
        { email: inputs.email.trim(), password: inputs.password },
        clientInstanceId,
      )

      replaceAuthSession({ ...result.data, clientInstanceId })
      navigate(ROUTES.HOME)
    } catch (error) {
      setSubmitError(getApiErrorMessage(
        error,
        '로그인 정보를 확인하지 못했어요. 이메일과 비밀번호를 다시 확인해 주세요.',
      ))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputKeyDown = (event) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
    event.preventDefault()
    reqSignInPage(event)
  }

  return (
    <AuthLayout>
      <S.Header>
        <h1>다시 만나서 반가워요</h1>
        <p>로그인하고 오늘의 학습을 이어가세요.</p>
      </S.Header>

      <S.Form noValidate onSubmit={reqSignInPage}>
        <S.Field>
          이메일
          <S.Input
            ref={emailInputRef}
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="name@example.com"
            value={inputs.email}
            $error={Boolean(fieldErrors.email)}
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby={fieldErrors.email ? 'sign-in-email-error' : undefined}
            onChange={updateInput}
            onKeyDown={handleInputKeyDown}
          />
          {fieldErrors.email && <S.FieldError id="sign-in-email-error">{fieldErrors.email}</S.FieldError>}
        </S.Field>

        <S.Field>
          비밀번호
          <S.PasswordControl>
            <S.Input
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="비밀번호를 입력해 주세요"
              value={inputs.password}
              $error={Boolean(fieldErrors.password)}
              aria-invalid={Boolean(fieldErrors.password)}
              aria-describedby={fieldErrors.password ? 'sign-in-password-error' : undefined}
              onChange={updateInput}
              onKeyDown={handleInputKeyDown}
            />
            <S.PasswordToggle
              type="button"
              aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((visible) => !visible)}
            >
              {showPassword
                ? <VisibilityOffOutlinedIcon aria-hidden="true" />
                : <VisibilityOutlinedIcon aria-hidden="true" />}
            </S.PasswordToggle>
          </S.PasswordControl>
          {fieldErrors.password && <S.FieldError id="sign-in-password-error">{fieldErrors.password}</S.FieldError>}
        </S.Field>

        {submitError && <S.ErrorNotice role="alert">{submitError}</S.ErrorNotice>}

        <S.SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? '로그인 중…' : '로그인'}
        </S.SubmitButton>
      </S.Form>

      <S.Assistance>
        <span>비밀번호가 기억나지 않나요?</span>
        <Link to={ROUTES.PASSWORD_RESET_EMAIL_VERIFICATION}>비밀번호 찾기</Link>
      </S.Assistance>
      <S.SignUpRow>
        아직 akive 회원이 아닌가요?
        <Link to={ROUTES.SIGN_UP}>회원가입</Link>
      </S.SignUpRow>
    </AuthLayout>
  )
}

export default SignInPage
